import { SubscriptionInfo } from '@/lib/api/dashboard/dashboard-api'
import {
    formatCredits,
    getAiCreditBalance,
    hasCreditBalanceToShow,
    type AiCreditBalance,
} from '@/lib/api/core/ai-credits-api'
import { useEffect, useState } from 'react'
import { MdAutoAwesome, MdCreditCard } from 'react-icons/md'
import { Link } from 'react-router-dom'

interface SubscriptionWidgetProps {
    subscription: SubscriptionInfo
}

export default function SubscriptionWidget({ subscription }: SubscriptionWidgetProps) {
    const trialDaysRemaining = subscription.trialDaysRemaining ?? 0
    const [creditBalance, setCreditBalance] = useState<AiCreditBalance | null>(null)

    useEffect(() => {
        void getAiCreditBalance().then(response => {
            if (response.success) setCreditBalance(response.result)
        })
    }, [])

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 overflow-hidden">
            <div className="bg-gradient-to-r from-secondary/10 to-accent/10 px-5 py-4 border-b border-base-300/50">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-secondary/20">
                        <MdCreditCard className="text-secondary text-lg" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-base-content">
                            Assinatura
                        </h3>
                        <p className="text-xs text-base-content/60">
                            {subscription.planName}
                        </p>
                    </div>
                </div>
            </div>

            <div className="p-5">
                {hasCreditBalanceToShow(creditBalance) && (
                    <Link
                        to="/system-store?product=ai-credits"
                        className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-base-200/60 px-3 py-2 hover:bg-base-200"
                    >
                        <span className="flex items-center gap-2 text-sm text-base-content/70">
                            <MdAutoAwesome className="text-primary" />
                            Créditos de IA
                        </span>
                        <span
                            className={`text-sm font-semibold tabular-nums ${
                                creditBalance.planCredits !== 'unlimited' && creditBalance.totalCredits <= 0
                                    ? 'text-error'
                                    : ''
                            }`}
                        >
                            {creditBalance.planCredits === 'unlimited'
                                ? 'Ilimitado'
                                : formatCredits(creditBalance.totalCredits)}
                        </span>
                    </Link>
                )}

                {/* Período grátis: o fim da cortesia precisa ser visível antes
                    de virar bloqueio de acesso. */}
                {trialDaysRemaining > 0 && (
                    <div className="mb-4 rounded-lg bg-secondary/10 px-3 py-2">
                        <p className="text-sm font-medium text-secondary">
                            Período grátis: {trialDaysRemaining}{' '}
                            {trialDaysRemaining === 1
                                ? 'dia restante'
                                : 'dias restantes'}
                        </p>
                        {subscription.trialEndsAt && (
                            <p className="text-xs text-base-content/60">
                                A primeira cobrança acontece em{' '}
                                {new Date(
                                    subscription.trialEndsAt
                                ).toLocaleDateString('pt-BR')}
                                .
                            </p>
                        )}
                    </div>
                )}

                <Link
                    to="/account?tab=financial"
                    className="btn btn-sm btn-outline w-full"
                >
                    Gerenciar Assinatura
                </Link>
            </div>
        </div>
    )
}
